import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { LogOut, Menu, Settings, User } from "lucide-react"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ThemeToggle } from "@/components/ui/theme-toggle"
import { cn } from "@/lib/utils"
import { AdminBar, adminBarOffset } from "@/components/AdminBar"
import { ADMIN_BG, adminNavSections, type NavItem } from "@/lib/admin-nav"
import type { AdminCounts, PageProps } from "@/types/inertia"

function SidebarNavItem({
  item,
  active,
}: {
  item: NavItem
  active: boolean
}) {
  const Icon = item.icon
  return (
    <Link
      href={item.href}
      className={cn(
        "flex items-center justify-between rounded-md px-3 py-2 text-sm no-underline transition-colors",
        active
          ? "bg-white/15 text-white"
          : "text-white/60 hover:bg-white/10 hover:text-white",
      )}
    >
      <span className="flex items-center gap-2.5">
        <Icon className="h-4 w-4 shrink-0" />
        {item.label}
      </span>
      {item.count !== undefined && (
        <span
          className={cn(
            "rounded px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
            active ? "bg-white/20 text-white" : "bg-white/10 text-white/50",
          )}
        >
          {item.count}
        </span>
      )}
    </Link>
  )
}

function SidebarSection({
  label,
  items,
  activeUrl,
}: {
  label: string
  items: NavItem[]
  activeUrl: string
}) {
  return (
    <div className="px-3">
      <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-white/30">
        {label}
      </p>
      <div className="flex flex-col gap-0.5">
        {items.map((item) => (
          <SidebarNavItem
            key={item.href}
            item={item}
            active={item.match(activeUrl)}
          />
        ))}
      </div>
    </div>
  )
}

function UserFooter({ email }: { email: string }) {
  const initial = email.charAt(0).toUpperCase() || "A"

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/50"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 text-[11px] font-semibold text-white">
            {initial}
          </span>
          <span className="min-w-0 flex-1 truncate text-xs text-white/70">
            {email || "Site Admin"}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-56">
        <DropdownMenuLabel className="normal-case tracking-normal">
          <span className="block text-[10px] uppercase tracking-wider text-ink-muted">
            Signed in as
          </span>
          <span className="block truncate text-xs font-medium text-ink-display">
            {email}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/profile" className="no-underline">
            <User /> Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/settings" className="no-underline">
            <Settings /> Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-2">
          <ThemeToggle block />
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          destructive
          onSelect={(e) => {
            e.preventDefault()
            router.delete("/logout")
          }}
        >
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SidebarContent({
  counts,
  email,
  url,
}: {
  counts: AdminCounts
  email: string
  url: string
}) {
  return (
    <>
      {/* Branding */}
      <div className="flex flex-col gap-1 border-b border-white/10 px-5 py-4">
        {/* The full lockup, light cut for the dark sidebar — the same artwork
            the public navs and footers carry. Flush: never rounded/cropped. */}
        <div className="min-w-0">
          <img
            src="/logos/sanctuary-lockup-light.png"
            alt="Sanctuary Therapy Centre"
            className="h-9 w-auto max-w-full object-contain object-left"
          />
          <p className="mt-1.5 text-[10px] uppercase tracking-widest text-white/40">
            ADMIN PORTAL v1.0
          </p>
        </div>
        <div className="mt-1 flex items-center gap-1.5 pl-1">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
          <span className="text-[11px] text-white/50">
            2 websites · {counts.sites} {counts.sites === 1 ? "location" : "locations"}
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-4">
        {adminNavSections(counts).map((section) => (
          <SidebarSection key={section.label} label={section.label} items={section.items} activeUrl={url} />
        ))}
      </nav>

      {/* User footer */}
      <div className="border-t border-white/10 px-2 py-3">
        <UserFooter email={email} />
      </div>
    </>
  )
}

/**
 * A job that gave up is the only trace of an enquiry that was never delivered,
 * and nothing else in the admin would ever mention it — so it says so on every
 * screen until somebody sends it again or discards it. Not dismissible on
 * purpose: it is a thing that was lost, not a notification.
 */
function FailedJobsBand({ count }: { count: number }) {
  return (
    <Link
      href="/admin/jobs"
      className="block border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 no-underline hover:bg-amber-100 sm:px-6 lg:px-8"
    >
      <strong className="font-medium">
        {count === 1 ? "1 background job failed" : `${count} background jobs failed`}
      </strong>{" "}
      — an email may not have arrived. Review and send again →
    </Link>
  )
}

export function AdminShell({ children }: { children: React.ReactNode }) {
  const { url, props } = usePage<
    PageProps & { admin_counts: AdminCounts; failed_jobs?: number }
  >()
  const failedJobs = props.failed_jobs ?? 0
  const email = props.current_user?.email ?? ""
  const counts = props.admin_counts ?? {
    therapists: 0, sites: 0, articles: 0,
    testimonials_gisela: 0, testimonials_sanctuary: 0,
    therapy_enquiries: 0, viewing_requests: 0,
    consultation_bookings_upcoming: 0, callback_requests_pending: 0,
    studio_pending: 0, newsletter_confirmed: 0,
  }
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false)

  // Close the drawer whenever navigation changes the page
  React.useEffect(() => {
    setMobileNavOpen(false)
  }, [url])

  return (
    <div className="flex min-h-screen flex-col" style={adminBarOffset(Boolean(props.admin_bar))}>
      <AdminBar variant="admin" />
      <div className="flex flex-1">
      {/* Sidebar (desktop) */}
      <aside
        className="sticky top-(--admin-bar-h) hidden h-[calc(100vh-var(--admin-bar-h))] w-62 shrink-0 flex-col lg:flex"
        style={{ backgroundColor: ADMIN_BG }}
      >
        <SidebarContent counts={counts} email={email} url={url} />
      </aside>

      {/* Main content */}
      <div className="flex min-h-screen min-w-0 flex-1 flex-col bg-page text-ink-body">
        {/* Top bar (mobile) */}
        <header
          className="sticky top-(--admin-bar-h) z-40 flex items-center gap-3 px-4 py-3 lg:hidden"
          style={{ backgroundColor: ADMIN_BG }}
        >
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-md text-white/80 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/50"
                aria-label="Open navigation menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="left"
              label="Admin navigation"
              hideClose
              className="text-white"
              style={{ backgroundColor: ADMIN_BG }}
            >
              <SidebarContent counts={counts} email={email} url={url} />
            </SheetContent>
          </Sheet>
          <div className="flex min-w-0 items-center gap-2.5">
            <img
              src="/logos/sanctuary-lockup-light.png"
              alt="Sanctuary Therapy Centre"
              className="h-8 w-auto min-w-0 object-contain object-left"
            />
            <span className="shrink-0 text-[10px] uppercase tracking-widest text-white/40">
              Admin
            </span>
          </div>
        </header>

        {failedJobs > 0 && !url.startsWith("/admin/jobs") && (
          <FailedJobsBand count={failedJobs} />
        )}

        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
      </div>
    </div>
  )
}
